import { useQuery, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import MANAGEMENT_HEADER from 'assets/images/user-management-header.webp';
import { BackgroundImg } from 'common/components/Img';
import { Loader } from 'common/components/Loader';
import { PageTitle } from 'common/components/PageTitle';
import { Tab, Tabs } from 'common/components/Tabs';
import { Alert } from 'common/components/Alert';
import {
    IRole,
    ArchivedUsersExpirationDaysDocument,
    CurrentUserDocument,
} from 'graphql/types';
import { BasePage } from 'hydra/pages/BasePage';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import { RouteLink } from 'route/components/RouteLink';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { UsersManagementTable } from 'user/components/UsersManagementTable';
import { useFrontendPermissions } from 'user/hooks';
import { useAppPermissions } from 'user/hooks/usePermissions';

import { AddUsersButton } from './AddUsersButton';

enum EUserStatus {
    ACTIVE = 'active',
    ARCHIVED = 'archived',
}

export const UsersManagementPage = () => {
    const [translate] = useTranslation();

    const {
        canUpdate: canUpdateUser,
        canDelete: canDeleteUser,
        loading: loadingPermissions,
    } = useFrontendPermissions('userManagement');

    const { canCreateUser = false } = useAppPermissions(
        'general',
        'management'
    );

    const { data: currentUserData, loading: currentUserLoading } =
        useQuery(CurrentUserDocument);

    const { currentUser } = currentUserData || {};

    const isUserActivePage = !!useRouteMatch('USER_MANAGEMENT_LIST');

    const userStatus = isUserActivePage
        ? EUserStatus.ACTIVE
        : EUserStatus.ARCHIVED;

    const isArchiveView = userStatus === EUserStatus.ARCHIVED;

    const {
        data: archivedUsersExpirationDaysData,
        loading: archivedUsersExpirationDaysLoading,
    } = useQuery(
        ArchivedUsersExpirationDaysDocument,
        isArchiveView ? {} : skipToken
    );

    const { archivedUsersExpirationDays } =
        archivedUsersExpirationDaysData?.organisation || {};

    const isManagerOrOwner =
        !!currentUser?.roles?.includes(IRole.Manager) ||
        !!currentUser?.roles?.includes(IRole.Owner);

    const loading =
        loadingPermissions ||
        currentUserLoading ||
        archivedUsersExpirationDaysLoading;

    const canManageUsers = !loadingPermissions && canUpdateUser;

    const canCreateUsers = !loadingPermissions && canCreateUser;

    if (
        (!loading && !canManageUsers) ||
        (!isManagerOrOwner && !isUserActivePage) // If archived page and not manager
    ) {
        return <FourOFourPage />;
    }

    const header = (
        <BackgroundImg
            src={MANAGEMENT_HEADER}
            sx={{
                height: '10vw',
                maxHeight: '150px',
                backgroundSize: 'contain',
                backgroundPosition: 'right top',
                display: { xs: 'none', sm: 'block' },
            }}
        />
    );

    return (
        <BasePage contentContainerMaxWidth="lg" header={header}>
            <PageTitle>
                {translate('usersManagementPage.browserTitle')}
            </PageTitle>
            <Box sx={{ pt: { xs: 4, sm: 2 } }}>
                <Grid
                    container
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Grid size="grow">
                        <Typography variant="h1">
                            {translate('usersManagementPage.pageTitle')}
                        </Typography>
                    </Grid>

                    {canCreateUsers && (
                        <Grid size="auto">
                            <AddUsersButton />
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Tabs sx={{ mt: 2 }} value={userStatus}>
                <Tab
                    component={RouteLink}
                    label={translate('usersManagementPage.tabs.active')}
                    to="USER_MANAGEMENT_LIST"
                    value={EUserStatus.ACTIVE}
                />

                {isManagerOrOwner && (
                    <Tab
                        component={RouteLink}
                        label={translate('usersManagementPage.tabs.archived')}
                        to="USER_MANAGEMENT_LIST_ARCHIVED"
                        value={EUserStatus.ARCHIVED}
                    />
                )}
            </Tabs>
            {archivedUsersExpirationDaysLoading && <Loader />}
            {!archivedUsersExpirationDaysLoading && (
                <>
                    {isArchiveView && (
                        <Alert severity="warning" sx={{ my: 2 }}>
                            {translate('usersManagementPage.archiveMessage', {
                                expiryDays: archivedUsersExpirationDays,
                            })}
                        </Alert>
                    )}

                    <Box sx={[isArchiveView ? { my: 0 } : { my: 2 }]}>
                        <UsersManagementTable
                            canDeleteUsers={canDeleteUser}
                            isEditable={canManageUsers}
                        />
                    </Box>
                </>
            )}
        </BasePage>
    );
};
